import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Store } from 'lucide-react';
import { api } from '@/App';
import { BrandCard } from '@/components/brands/BrandCard';

const fetcher = (url) => api.get(url).then((res) => res.data);

/**
 * BrandWorld — the Support-a-Brand archetype content on Browse.
 * Shows the approved-brand grid (linking to /brands/[slug]) plus the apply CTA.
 */
export default function BrandWorld({ archetype, accent, textAccent, isDark }) {
  const { data: brands = [], isLoading } = useSWR('/brands', fetcher);

  return (
    <div data-testid="archetype-world-support_a_brand">
      {brands.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: isDark ? accent : textAccent }}>
            The Directory
          </div>
          <h2
            style={{
              fontFamily: 'Playfair Display, serif',
              fontWeight: 600,
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              color: isDark ? '#FFFAF5' : '#20212B',
            }}
          >
            Small businesses worth knowing
          </h2>
          <p className="text-sm" style={{ color: isDark ? 'rgba(255,250,245,0.7)' : 'rgba(83,65,69,0.8)' }}>
            Hand-reviewed makers and indie brands — every visit supports them directly.
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-2xl animate-pulse" style={{ aspectRatio: '4 / 5', background: `${accent}20` }} />
          ))}
        </div>
      ) : brands.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10" data-testid="brand-world-grid">
          {brands.slice(0, 8).map((brand) => (
            <BrandCard key={brand.id} brand={brand} isDark={isDark} />
          ))}
        </div>
      ) : (
        <div
          data-testid="brand-world-empty"
          className="rounded-2xl p-8 sm:p-14 text-center mb-10"
          style={{
            background: isDark
              ? `linear-gradient(135deg, ${accent}30 0%, rgba(45,67,84,0.6) 100%)`
              : `linear-gradient(135deg, ${accent}25 0%, rgba(255,250,245,0.95) 100%)`,
            border: `1px solid ${accent}55`,
          }}
        >
          <div className="max-w-2xl mx-auto">
            <div
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold mb-4 px-3 py-1 rounded-full"
              style={{ color: textAccent, background: `${accent}22`, border: `1px solid ${accent}55` }}
            >
              <Sparkles size={12} />
              Now accepting brands
            </div>
            <h2
              className="mb-3"
              style={{
                fontFamily: archetype?.fontFamily,
                ...(archetype?.titleStyle || {}),
                fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                lineHeight: 1.1,
                color: isDark ? '#FFFAF5' : '#20212B',
              }}
            >
              The first names are being reviewed
            </h2>
            <p
              className="text-sm sm:text-base mb-6"
              style={{ color: isDark ? 'rgba(255,250,245,0.75)' : 'rgba(83,65,69,0.85)', fontFamily: 'Playfair Display, serif' }}
            >
              A home for the makers, founders, and indie brands worth knowing. Own a small business? Be one of the founding names here.
            </p>
          </div>
        </div>
      )}

      {/* Closing CTA — view all + apply */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        style={{
          background: isDark
            ? `linear-gradient(135deg, ${accent}40 0%, rgba(45,67,84,0.6) 100%)`
            : `linear-gradient(135deg, ${accent}40 0%, rgba(255,250,245,0.9) 100%)`,
          border: `1px solid ${accent}66`,
        }}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Store size={16} style={{ color: isDark ? accent : textAccent }} />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: isDark ? accent : textAccent }}>
              Small Businesses
            </span>
          </div>
          <h3
            className="mb-1"
            style={{
              fontFamily: archetype?.fontFamily,
              ...(archetype?.titleStyle || {}),
              fontSize: '22px',
              color: isDark ? '#FFFAF5' : '#20212B',
            }}
          >
            Own a brand?
          </h3>
          <p className="text-sm max-w-md" style={{ color: isDark ? 'rgba(255,250,245,0.75)' : 'rgba(83,65,69,0.85)' }}>
            Apply to be featured in the directory — it's free, and approved brands get their own Mementa page.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5">
          {brands.length > 0 && (
            <Link
              to="/brands"
              data-testid="brand-world-view-all"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap transition-transform hover:scale-105"
              style={{ background: 'transparent', color: isDark ? accent : textAccent, border: `1.5px solid ${accent}` }}
            >
              View the full directory
              <ArrowRight size={14} />
            </Link>
          )}
          <Link
            to="/brands/apply"
            data-testid="support-a-brand-apply-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap transition-transform hover:scale-105"
            style={{ background: accent, color: '#FFFAF5' }}
          >
            Apply to be featured
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
